/**
 * Utilidades de tiempo en America/Bogota (UTC−05:00 fijo, sin horario de verano).
 * El dominio nunca llama a `new Date()` sin argumentos: el "ahora" lo inyecta RelojService.
 */
import { DIA_SEMANA_META, DIAS_SEMANA_ORDEN, DiaSemana, GrupoEntity, SesionEntity } from '../models/asistencia.model';

const OFFSET_MS = -5 * 60 * 60 * 1000;
const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const DIAS_LARGOS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** Valida formato estricto 'HH:mm' en reloj de 24 horas (00:00 a 23:59). */
export function esHoraValida(hora: string | undefined): boolean {
  return !!hora && /^([01]\d|2[0-3]):[0-5]\d$/.test(hora);
}

/** Convierte 'HH:mm' a minutos desde las 00:00. */
export function minutosDeHora(hora: string): number {
  const [h, m] = hora.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Duración en minutos entre horaInicio y horaFin (positivo si horaFin > horaInicio). */
export function duracionMinutos(horaInicio: string, horaFin: string): number {
  if (!esHoraValida(horaInicio) || !esHoraValida(horaFin)) return 0;
  return minutosDeHora(horaFin) - minutosDeHora(horaInicio);
}

/** Ordena días de lunes a domingo sin duplicados. */
export function normalizarDiasSemana(dias: DiaSemana[]): DiaSemana[] {
  const set = new Set(dias);
  return DIAS_SEMANA_ORDEN.filter(d => set.has(d));
}

/** Genera la etiqueta legible de días a partir del arreglo tipado DiaSemana[]. */
export function formatearDiasSemana(dias: DiaSemana[]): string {
  const ord = normalizarDiasSemana(dias);
  if (!ord.length) return 'Por definir';
  if (ord.length === 1) return DIA_SEMANA_META[ord[0]].largo;
  const cortos = ord.map((d, idx) => idx === 0 ? DIA_SEMANA_META[d].corto : DIA_SEMANA_META[d].corto.toLowerCase());
  if (cortos.length === 2) return `${cortos[0]} y ${cortos[1]}`;
  return `${cortos.slice(0, -1).join(', ')} y ${cortos[cortos.length - 1]}`;
}

/** Genera la etiqueta legible de franja horaria a partir de horas estructuradas HH:mm. */
export function formatearFranjaGrupo(
  horaInicio: string,
  horaFin: string,
  dobleJornada?: boolean,
  horaInicio2?: string,
  horaFin2?: string
): string {
  const b1 = `${horaInicio.replace(/^0/, '')}–${horaFin.replace(/^0/, '')}`;
  if (dobleJornada && horaInicio2 && horaFin2) {
    const b2 = `${horaInicio2.replace(/^0/, '')}–${horaFin2.replace(/^0/, '')}`;
    return `${b1} y ${b2}`;
  }
  return b1;
}

/** Extrae o infiere los campos estructurados de horario de un grupo existente. */
export function inferirHorarioGrupo(g: GrupoEntity): {
  diasSemana: DiaSemana[];
  horaInicio: string;
  horaFin: string;
  dobleJornada: boolean;
  horaInicio2: string;
  horaFin2: string;
} {
  if (g.diasSemana?.length && g.horaInicio && g.horaFin) {
    return {
      diasSemana: normalizarDiasSemana(g.diasSemana),
      horaInicio: g.horaInicio,
      horaFin: g.horaFin,
      dobleJornada: !!g.dobleJornada,
      horaInicio2: g.horaInicio2 ?? '14:00',
      horaFin2: g.horaFin2 ?? '16:00'
    };
  }
  const dl = g.diasLabel.toLowerCase();
  let diasSemana: DiaSemana[] = ['MAR', 'JUE'];
  if (dl.includes('sáb') || dl.includes('sab')) diasSemana = ['SAB'];
  else if (dl.includes('mié') || dl.includes('mie')) diasSemana = ['MIE', 'VIE'];
  else if (dl.includes('lun')) diasSemana = ['LUN', 'MIE'];

  const partes = g.franjaLabel.split(/\s+y\s+/i);
  const parsearBloque = (txt: string, defI: string, defF: string): [string, string] => {
    const m = txt.match(/(\d{1,2}:\d{2})\s*[–-]\s*(\d{1,2}:\d{2})/);
    if (!m) return [defI, defF];
    const pad = (h: string) => (h.length === 4 ? `0${h}` : h);
    return [pad(m[1]), pad(m[2])];
  };
  const [horaInicio, horaFin] = parsearBloque(partes[0] ?? '', '09:00', '11:00');
  const dobleJornada = partes.length > 1 || diasSemana.length === 1;
  const [horaInicio2, horaFin2] = partes[1] ? parsearBloque(partes[1], '14:00', '16:00') : ['14:00', '16:00'];
  return { diasSemana, horaInicio, horaFin, dobleJornada, horaInicio2, horaFin2 };
}

/** Instante absoluto de una fecha y hora locales de Bogotá. */
export function instante(fecha: string, hora: string): Date {
  const f = typeof fecha === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fecha.trim()) ? fecha.trim() : '2026-10-26';
  const h = typeof hora === 'string' && /^\d{2}:\d{2}$/.test(hora.trim()) ? hora.trim() : '12:00';
  const d = new Date(`${f}T${h}:00-05:00`);
  return Number.isNaN(d.getTime()) ? new Date('2026-10-26T12:00:00-05:00') : d;
}

function desplazado(d: Date): Date {
  const t = d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : Date.now();
  return new Date(t + OFFSET_MS);
}

export function fechaBogota(d: Date): string {
  return desplazado(d).toISOString().slice(0, 10);
}

export function horaBogota(d: Date): string {
  return desplazado(d).toISOString().slice(11, 16);
}

export function diaSemanaBogota(d: Date): number {
  return desplazado(d).getUTCDay();
}

export function sumarMinutos(d: Date, minutos: number): Date {
  const t = d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : Date.now();
  const min = Number.isFinite(minutos) ? minutos : 0;
  return new Date(t + min * 60_000);
}

export function sumarHoras(d: Date, horas: number): Date {
  const h = Number.isFinite(horas) ? horas : 0;
  return sumarMinutos(d, h * 60);
}

export function sumarDias(fecha: string, dias: number): string {
  const d = instante(fecha, '12:00');
  const delta = Number.isFinite(dias) ? dias : 0;
  return fechaBogota(new Date(d.getTime() + delta * 86_400_000));
}

export function inicioSesion(s: Pick<SesionEntity, 'fecha' | 'horaInicio'>): Date {
  return instante(s.fecha, s.horaInicio);
}

export function finSesion(s: Pick<SesionEntity, 'fecha' | 'horaFin'>): Date {
  return instante(s.fecha, s.horaFin);
}

/** ¿Se solapan dos franjas de la misma fecha? */
export function seSolapan(
  a: Pick<SesionEntity, 'fecha' | 'horaInicio' | 'horaFin'>,
  b: Pick<SesionEntity, 'fecha' | 'horaInicio' | 'horaFin'>
): boolean {
  if (a.fecha !== b.fecha) return false;
  return a.horaInicio < b.horaFin && b.horaInicio < a.horaFin;
}

export function enHorarioSilencioso(d: Date, desde: string, hasta: string): boolean {
  const h = horaBogota(d);
  return desde > hasta ? h >= desde || h < hasta : h >= desde && h < hasta;
}

/** Próximo instante (>= d) en el que el reloj de Bogotá marca `hora`. */
export function proximaHora(d: Date, hora: string): Date {
  const hoy = instante(fechaBogota(d), hora);
  return hoy.getTime() >= d.getTime() ? hoy : instante(sumarDias(fechaBogota(d), 1), hora);
}

/** Número ISO de semana relativo: lunes de la semana de `fecha`. */
export function lunesDe(fecha: string): string {
  const dia = diaSemanaBogota(instante(fecha, '12:00'));
  const delta = dia === 0 ? -6 : 1 - dia;
  return sumarDias(fecha, delta);
}

export function formatoFechaCorta(fecha: string): string {
  const d = instante(fecha, '12:00');
  return `${DIAS[diaSemanaBogota(d)]} ${fecha.slice(8, 10)}/${fecha.slice(5, 7)}`;
}

export function formatoFechaLarga(fecha: string): string {
  const d = instante(fecha, '12:00');
  return `${DIAS_LARGOS[diaSemanaBogota(d)]} ${Number(fecha.slice(8, 10))}/${fecha.slice(5, 7)}/${fecha.slice(0, 4)}`;
}

export function formatoHora12(hora: string): string {
  const [h, m] = hora.split(':').map(Number);
  const sufijo = h < 12 ? 'a. m.' : 'p. m.';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${sufijo}`;
}

export function formatoFranja(horaInicio: string, horaFin: string): string {
  return `${horaInicio.replace(/^0/, '')}–${horaFin.replace(/^0/, '')}`;
}

export function formatoInstante(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const f = fechaBogota(d);
  return `${f.slice(8, 10)}/${f.slice(5, 7)}/${f.slice(0, 4)} ${horaBogota(d)}`;
}

export function horasEntre(a: Date, b: Date): number {
  return (b.getTime() - a.getTime()) / 3_600_000;
}

const JS_DAY_TO_DIA_SEMANA: Record<number, DiaSemana> = {
  0: 'DOM',
  1: 'LUN',
  2: 'MAR',
  3: 'MIE',
  4: 'JUE',
  5: 'VIE',
  6: 'SAB'
};

/** Devuelve el código DiaSemana ('LUN'..'DOM') de una fecha 'YYYY-MM-DD' en zona Bogotá. */
export function diaSemanaDeFecha(fecha: string): DiaSemana {
  const d = instante(fecha, '12:00');
  return JS_DAY_TO_DIA_SEMANA[diaSemanaBogota(d)] ?? 'LUN';
}

/** Suma `minutos` a una hora 'HH:mm' y devuelve la hora resultante en 'HH:mm' (limitada a 23:45). */
export function sumarMinutosHora(horaInicio: string, minutos: number): string {
  if (!esHoraValida(horaInicio)) return '11:00';
  const total = Math.max(15, Math.min(23 * 60 + 45, minutosDeHora(horaInicio) + minutos));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Calcula el número de semana relativa (>= 1) de `fechaObjetivo` con respecto al lunes de `fechaReferenciaInicio`.
 */
export function calcularSemanaRelativa(fechaReferenciaInicio: string, fechaObjetivo = '2026-10-26'): number {
  const objValida = typeof fechaObjetivo === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaObjetivo) ? fechaObjetivo : '2026-10-26';
  const refValida = typeof fechaReferenciaInicio === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaReferenciaInicio)
    ? fechaReferenciaInicio
    : objValida;
  const lunesFecha = instante(lunesDe(objValida), '12:00').getTime();
  const lunesRef = instante(lunesDe(refValida), '12:00').getTime();
  const diffSemanas = Math.round((lunesFecha - lunesRef) / (7 * 86_400_000));
  return Math.max(1, diffSemanas + 1);
}

export interface SlotSesionProgramada {
  numero: number;
  semana: number;
  fecha: string;
  horaInicio: string;
  horaFin: string;
}

/**
 * Genera el calendario de `totalClases` sesiones (1..N) a partir de `fechaInicioGrupo` (YYYY-MM-DD),
 * recorriendo los días reales del calendario que coincidan con `horario.diasSemana`.
 * Soporta cualquier cantidad de días por semana (1..7) y doble jornada en el mismo día (ej. sábado mañana y tarde).
 */
export function calcularSlotsSesionesGrupo(
  fechaInicioGrupo: string,
  totalClases: number,
  horario: {
    diasSemana: DiaSemana[];
    horaInicio: string;
    horaFin: string;
    dobleJornada?: boolean;
    horaInicio2?: string;
    horaFin2?: string;
  },
  fechaReferenciaCiclo?: string
): SlotSesionProgramada[] {
  const cantidad = Math.max(0, Math.min(100, Math.floor(totalClases || 0)));
  if (cantidad === 0) return [];
  const dias = normalizarDiasSemana(horario.diasSemana?.length ? horario.diasSemana : ['MAR', 'JUE']);
  const setDias = new Set<DiaSemana>(dias);
  const fechaBase = /^\d{4}-\d{2}-\d{2}$/.test(fechaInicioGrupo) ? fechaInicioGrupo : '2026-10-26';
  const refCiclo = fechaReferenciaCiclo && /^\d{4}-\d{2}-\d{2}$/.test(fechaReferenciaCiclo)
    ? fechaReferenciaCiclo
    : fechaBase;

  const out: SlotSesionProgramada[] = [];
  let cursor = fechaBase;
  let guard = 0;

  while (out.length < cantidad && guard < 730) {
    const dia = diaSemanaDeFecha(cursor);
    if (setDias.has(dia)) {
      const semana = calcularSemanaRelativa(refCiclo, cursor);
      out.push({
        numero: out.length + 1,
        semana,
        fecha: cursor,
        horaInicio: horario.horaInicio,
        horaFin: horario.horaFin
      });
      if (
        out.length < cantidad &&
        horario.dobleJornada &&
        horario.horaInicio2 &&
        horario.horaFin2 &&
        esHoraValida(horario.horaInicio2) &&
        esHoraValida(horario.horaFin2)
      ) {
        out.push({
          numero: out.length + 1,
          semana,
          fecha: cursor,
          horaInicio: horario.horaInicio2,
          horaFin: horario.horaFin2
        });
      }
    }
    cursor = sumarDias(cursor, 1);
    guard++;
  }

  return out;
}

/**
 * Calcula la fecha (YYYY-MM-DD), horaInicio (HH:mm) y horaFin (HH:mm) de la sesión `numero` (1..N)
 * de un grupo a partir de la fecha de inicio y la configuración estructurada del grupo.
 */
export function calcularSlotSesionGrupo(
  fechaInicio: string,
  numeroSesion: number,
  horario: {
    diasSemana: DiaSemana[];
    horaInicio: string;
    horaFin: string;
    dobleJornada?: boolean;
    horaInicio2?: string;
    horaFin2?: string;
  },
  fechaReferenciaCiclo?: string
): { semana: number; fecha: string; horaInicio: string; horaFin: string } {
  const n = Math.max(1, Math.floor(numeroSesion || 1));
  const slots = calcularSlotsSesionesGrupo(fechaInicio, n, horario, fechaReferenciaCiclo);
  const slot = slots[n - 1] ?? slots[slots.length - 1];
  if (slot) {
    return {
      semana: slot.semana,
      fecha: slot.fecha,
      horaInicio: slot.horaInicio,
      horaFin: slot.horaFin
    };
  }
  return {
    semana: 1,
    fecha: fechaInicio,
    horaInicio: horario.horaInicio,
    horaFin: horario.horaFin
  };
}

export const MESES_NOMBRES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
] as const;

export const MESES_CORTOS = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
] as const;

export interface InfoSemanaMes {
  id: string;            // Lunes ISO 'YYYY-MM-DD'
  anioMes: string;       // 'YYYY-MM'
  anio: number;
  mesNumero: number;     // 1..12
  mesNombre: string;     // 'Octubre'
  mesCorto: string;      // 'Oct'
  semanaDelMes: number;  // 1..5 (dentro del mes)
  etiqueta: string;      // 'Semana 1'
  etiquetaCorta: string; // 'Sem. 1 · Oct'
  etiquetaMedia: string; // 'Semana 1 de oct.'
  etiquetaLarga: string; // 'Semana 1 de octubre 2026'
  rango: string;         // 'lun 26/10 – sáb 31/10'
  rangoCorto: string;    // '26/10 – 31/10'
  desde: string;         // Lunes 'YYYY-MM-DD'
  hasta: string;         // Sábado 'YYYY-MM-DD'
  hastaDomingo: string;  // Domingo 'YYYY-MM-DD'
}

/** Normaliza una cadena 'YYYY-MM' o 'YYYY-MM-DD' a 'YYYY-MM'. */
export function normalizarAnioMes(valor: string | undefined, fallback = '2026-10'): string {
  if (typeof valor === 'string' && /^\d{4}-(0[1-9]|1[0-2])/.test(valor.trim())) {
    return valor.trim().slice(0, 7);
  }
  return fallback;
}

/** Suma o resta `deltaMeses` a un mes 'YYYY-MM' y retorna el nuevo 'YYYY-MM'. */
export function sumarMeses(anioMes: string, deltaMeses: number): string {
  const norm = normalizarAnioMes(anioMes);
  const [yStr, mStr] = norm.split('-');
  const totalMeses = (Number(yStr) * 12) + (Number(mStr) - 1) + (Number.isFinite(deltaMeses) ? deltaMeses : 0);
  const nuevoAnio = Math.floor(totalMeses / 12);
  const nuevoMes = ((totalMeses % 12) + 12) % 12 + 1;
  return `${String(nuevoAnio).padStart(4, '0')}-${String(nuevoMes).padStart(2, '0')}`;
}

/** Formatea 'YYYY-MM' como 'Octubre 2026'. */
export function formatearMesAnio(anioMes: string): string {
  const norm = normalizarAnioMes(anioMes);
  const [yStr, mStr] = norm.split('-');
  const idx = Math.max(0, Math.min(11, Number(mStr) - 1));
  return `${MESES_NOMBRES[idx]} ${yStr}`;
}

/**
 * Devuelve las semanas (lunes a domingo) que pertenecen o tienen días hábiles (lun-sáb) dentro del mes `anioMes` ('YYYY-MM').
 * Cada mes tiene entre 4 y 5 semanas numeradas del 1 al 5 dentro de ese mes.
 */
export function obtenerSemanasDelMes(anioMes: string): InfoSemanaMes[] {
  const norm = normalizarAnioMes(anioMes);
  const [yStr, mStr] = norm.split('-');
  const anio = Number(yStr);
  const mesNumero = Number(mStr);
  const idxMes = Math.max(0, Math.min(11, mesNumero - 1));
  const mesNombre = MESES_NOMBRES[idxMes];
  const mesCorto = MESES_CORTOS[idxMes];

  const primerDiaMes = `${norm}-01`;
  const ultimoDiaNum = new Date(Date.UTC(anio, mesNumero, 0)).getUTCDate();
  const ultimoDiaMes = `${norm}-${String(ultimoDiaNum).padStart(2, '0')}`;

  // Si el día 1 del mes cae domingo, el bloque lun-sáb previo pertenece íntegro al mes anterior;
  // la Semana 1 del mes inicia el lunes 2.
  const diaPrimero = diaSemanaDeFecha(primerDiaMes);
  let cursorLunes = diaPrimero === 'DOM' ? sumarDias(primerDiaMes, 1) : lunesDe(primerDiaMes);

  const semanas: InfoSemanaMes[] = [];
  let num = 1;
  while (cursorLunes <= ultimoDiaMes && num <= 6) {
    const desde = cursorLunes;
    const hasta = sumarDias(desde, 5);
    const hastaDomingo = sumarDias(desde, 6);
    const rangoCorto = `${desde.slice(8, 10)}/${desde.slice(5, 7)} – ${hasta.slice(8, 10)}/${hasta.slice(5, 7)}`;
    semanas.push({
      id: desde,
      anioMes: norm,
      anio,
      mesNumero,
      mesNombre,
      mesCorto,
      semanaDelMes: num,
      etiqueta: `Semana ${num}`,
      etiquetaCorta: `Sem. ${num} · ${mesCorto}`,
      etiquetaMedia: `Semana ${num} de ${mesCorto.toLowerCase()}.`,
      etiquetaLarga: `Semana ${num} de ${mesNombre.toLowerCase()} ${anio}`,
      rango: `${formatoFechaCorta(desde)} – ${formatoFechaCorta(hasta)}`,
      rangoCorto,
      desde,
      hasta,
      hastaDomingo
    });
    cursorLunes = sumarDias(cursorLunes, 7);
    num++;
  }
  return semanas;
}

/**
 * Resuelve la información de semana dentro del mes para una fecha dada ('YYYY-MM-DD').
 * Reutilizable en tablas, filtros, formularios de creación/edición y calendario.
 */
export function describirSemanaDeFecha(fechaIso: string): InfoSemanaMes {
  const f = typeof fechaIso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaIso.trim())
    ? fechaIso.trim()
    : '2026-10-26';
  const lunes = lunesDe(f);
  // Si la fecha es domingo día 01 del mes, su semana lun-dom pertenece a la última semana del mes de su lunes.
  const anioMes = f.endsWith('-01') && diaSemanaDeFecha(f) === 'DOM'
    ? lunes.slice(0, 7)
    : f.slice(0, 7);
  const semanas = obtenerSemanasDelMes(anioMes);
  return (
    semanas.find(s => s.id === lunes) ??
    semanas.find(s => f >= s.desde && f <= s.hastaDomingo) ??
    semanas[0]
  );
}



